import createLogger from "../../utils/logger.util";
import { Language } from "../../assets/languages/langs";
import { SortingOrder } from "../../types/SortingOrder";
import { Base } from "..";

const logger = createLogger({ name: "Modal" });

class Modal extends Base {
	public static readonly ConstantSupportModalType = {
		Dispatcher: "dispatcher",
		Sector: "sector",
		Agent: "agent",
		Counterparty: "counterparty",
		Client: "client",
		CustomerDiscountPlan: "customer_discount_plan",
	} as const;

	public static readonly SUPPORT_MODAL: Modal.ValueSupportModal[] = [
		Modal.ConstantSupportModalType.Agent,
		Modal.ConstantSupportModalType.Client,
		Modal.ConstantSupportModalType.Counterparty,
		Modal.ConstantSupportModalType.Dispatcher,
		Modal.ConstantSupportModalType.Sector,
	];

	private static optionsToRequest(options: Modal.SubscribeOptions) {
		return {
			limit: options.limit,
			offset: options.offset,

			typedb: options.typedb,
			lang: options.lang,
			query: options.query,

			pick: options.pick,
			omit: options.omit,
			taxiServiceIds: options.taxiServiceIds,
			onlyIds: options.onlyIds,

			sort: options.sort,
			subscribeType: options.subscribeType,
		};
	}

	public static async getAll<T>(
		params: Modal.SubscribeOptions & { typedb: Modal.ValueSupportModal },
	): Promise<Modal.Response<T> | null> {
		try {
			const response: Modal.Response<T> = await this.request((prpc) =>
				prpc.theirsModel.modals.getAll(this.optionsToRequest(params)),
			);

			logger.info(`[${params.typedb} Modal] getAll`, {
				params,
				data: response,
			});
			return response;
		} catch (error) {
			logger.error(`[${params.typedb} Modal] error getAll:`, {
				error,
				params,
			});
			return null;
		}
	}

	public static getAllDispatcher(
		object: Omit<Modal.SubscribeOptions, "typedb">,
	) {
		return Modal.getAll<Modal.ModelSimpleForMultySelectModalDispatcher>({
			...object,
			typedb: Modal.ConstantSupportModalType.Dispatcher,
		});
	}

	public static getAllSector(object: Omit<Modal.SubscribeOptions, "typedb">) {
		return Modal.getAll<Modal.ModelSimpleForMultySelectModalSector>({
			...object,
			typedb: Modal.ConstantSupportModalType.Sector,
		});
	}

	public static getAllAgent(object: Omit<Modal.SubscribeOptions, "typedb">) {
		return Modal.getAll<Modal.ModelSimpleForMultySelectModalAgent>({
			...object,
			typedb: Modal.ConstantSupportModalType.Agent,
		});
	}

	public static getAllCounterparty(
		object: Omit<Modal.SubscribeOptions, "typedb">,
	) {
		return Modal.getAll<Modal.ModelSimpleForMultySelectModalCounterparty>({
			...object,
			typedb: Modal.ConstantSupportModalType.Counterparty,
		});
	}

	public static getAllClient(object: Omit<Modal.SubscribeOptions, "typedb">) {
		return Modal.getAll<Modal.ModelSimpleForMultySelectModalClient>({
			...object,
			typedb: Modal.ConstantSupportModalType.Client,
		});
	}

	public static getAllCustomerForDiscountPlan(
		object: Omit<Modal.SubscribeOptions, "typedb">,
	) {
		return Modal.getAll<Modal.ModelForModalCustomerDiscountPlan>({
			...object,
			typedb: Modal.ConstantSupportModalType.CustomerDiscountPlan,
		});
	}

	public static getAllCustomerIdsForDiscountPlan(
		object: Omit<Modal.SubscribeOptions, "typedb">,
	) {
		return Modal.getAll<
			Pick<Modal.ModelForModalCustomerDiscountPlan, "id">
		>({
			...object,
			onlyIds: true,
			typedb: Modal.ConstantSupportModalType.CustomerDiscountPlan,
		});
	}
}

declare namespace Modal {
	type TypeSupportModal = typeof Modal.ConstantSupportModalType;
	type ValueSupportModal =
		(typeof Modal.ConstantSupportModalType)[keyof TypeSupportModal];

	interface SubscribeOptions {
		limit?: number;
		offset?: number;
		typedb: ValueSupportModal;
		lang?: string;
		query?: string;
		pick?: string[];
		omit?: string[];
		taxiServiceIds?: number[];
		onlyIds?: boolean;
		sort?: { [key: string]: string; type: SortingOrder };
		subscribeType?: string;
		// order?: { [key: string]: SortingOrder };
	}

	interface ModelSimpleForMultySelectModalDispatcher {
		alias: string;
		id: number;
		lastName: string;
		firstName: string;
		fatherName: string;
	}

	interface ModelSimpleForMultySelectModalSector {
		id: number;
		name: Record<Language, string> | string; //  if has lang is string
	}

	interface ModelSimpleForMultySelectModalAgent {
		id: number;
		name: Record<Language, string> | string; //  if has lang is string
		codeValue: string;
	}

	interface ModelSimpleForMultySelectModalCounterparty {
		id: number;
		name: string;
		edrpou: string;
		checks: {
			id: number;
			checkValue: string;
			checkCards: {
				id: number;
				codeValue: string;
			}[];
		}[];
	}

	interface ModelSimpleForMultySelectModalClient {
		id: number;
		lastName: string;
		firstName: string;
		fatherName: string;
		phone: string;
	}

	interface ModelForModalCustomerDiscountPlan {
		id: number;
		lastName: string;
		firstName: string;
		fatherName: string;
		phone: string;
		isApp: boolean;
		taxiServiceId: number;
		taxiServiceName?: string;
	}

	interface Pagination {
		offset: number;
		count: number;
	}

	interface Response<T> {
		items: T[];
		pagination: Pagination;
	}
}

export default Modal;
